/**
 * VÉRAULT Member Suite — AgreementSignModal (Wave 27.5 · Phase 4e).
 * ────────────────────────────────────────────────────────────────────
 * Calm in-portal e-signature modal. Submits to
 * POST /api/client-portal/agreements/{id}/sign.
 *
 *   Typed-name signature · explicit acknowledgement checkbox · clear
 *   legal copy block. No external redirects, no magic-link round-trips.
 */
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileSignature, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../../index";
import { useOnlineState } from "../../../../components/OnlineProvider";

export default function AgreementSignModal({ agreement, onClose, onSigned }) {
  const [typedName, setTypedName] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { isOnline } = useOnlineState();
  const canSubmit = typedName.trim().length >= 2 && acknowledged && !submitting && isOnline;

  const handleSign = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/client-portal/agreements/${agreement.id}/sign`, {
        typed_name: typedName.trim(),
        acknowledged: true,
      });
      toast.success("Agreement signed.", {
        description: `Your signature has been recorded.`,
      });
      onSigned?.(res.data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't record your signature.";
      toast.error("Unable to sign", { description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-50 bg-black/65 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
        onClick={onClose}
        data-testid="agreement-sign-modal"
      >
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-2xl rounded-lg overflow-hidden bg-[hsl(var(--background))] shadow-2xl"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 inline-flex items-center justify-center size-9 rounded-full hover:bg-[hsl(var(--background-paper))]"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>

          <div className="px-7 sm:px-10 py-8 sm:py-10">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="inline-flex items-center justify-center size-9 rounded-full bg-[hsl(var(--accent-gold)/0.12)]">
                <FileSignature className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              </span>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
                Review &amp; sign
              </p>
            </div>

            <h2 className="font-serif text-[24px] sm:text-[28px] leading-tight tracking-tight">
              {agreement.title || "Agreement"}
            </h2>
            <p className="mt-2 font-accent italic text-[14px] text-[hsl(var(--foreground-secondary))]">
              A quiet, considered moment. Please read carefully before signing.
            </p>

            {/* Legal acknowledgement block */}
            <div className="mt-6 rounded-md border-l-2 border-[hsl(var(--accent-gold)/0.6)] bg-[hsl(var(--background-paper))] px-5 py-4">
              <p className="font-accent italic text-[13.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]">
                By typing my name below, I acknowledge that I have reviewed
                the terms of this {(agreement.agreement_type || "agreement").replace("_", " ")}, that I am
                authorized to bind myself to its terms, and that this
                electronic signature carries the same legal weight as a
                handwritten signature. I confirm that the information I have
                provided is accurate to the best of my knowledge.
              </p>
            </div>

            {/* Typed-name signature input */}
            <div className="mt-6">
              <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-2" htmlFor="agreement-sign-name">
                Type your full name to sign
              </label>
              <input
                id="agreement-sign-name"
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Jonathan Jackson"
                className="w-full px-4 py-2.5 rounded-md border border-[hsl(var(--border))] bg-white/60 font-serif text-[20px] italic placeholder:text-[hsl(var(--foreground-muted))] focus:border-[hsl(var(--accent-gold))] focus:outline-none transition-colors"
                autoFocus
                data-testid="sign-typed-name-input"
              />
              {typedName && (
                <p className="mt-2 font-accent italic text-[18px] text-[hsl(var(--foreground))] border-b border-[hsl(var(--accent-gold)/0.5)] pb-1 inline-block min-w-[12rem]" style={{ fontFamily: "'Cedarville Cursive', cursive" }}>
                  {typedName}
                </p>
              )}
            </div>

            {/* Explicit ack checkbox */}
            <label className="mt-5 flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-0.5 size-4 accent-[hsl(var(--accent-gold))]"
                data-testid="sign-ack-checkbox"
              />
              <span className="font-serif text-[14px] text-[hsl(var(--foreground-secondary))] leading-snug">
                I acknowledge and agree to the terms above. I understand
                this signature is legally binding.
              </span>
            </label>

            {/* Actions */}
            <div className="mt-7 flex flex-wrap items-center gap-2 justify-end">
              {!isOnline ? (
                <span
                  className="mr-auto text-[11px] italic text-[hsl(var(--foreground-muted))]"
                  data-testid="sign-offline-hint"
                >
                  Available when you reconnect.
                </span>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-md font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSign}
                disabled={!canSubmit}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-mono text-[10px] uppercase tracking-[0.22em] bg-[hsl(var(--foreground))] text-[hsl(var(--background))] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[hsl(var(--accent-gold))] transition-colors"
                data-testid="sign-submit"
              >
                {submitting && <Loader2 className="size-3 animate-spin" strokeWidth={2} />}
                Sign &amp; submit
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
